import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';

/**
 * Store application review (`/admin/store-approvals`, storeApprovalService).
 *
 * Kept beside brandDirectoryApi rather than inside it: brand claims and store
 * applications are different records with different state machines, and the
 * Approvals queue shows both side by side.
 */

export interface StoreApplicationAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  country?: string;
}

/** One row of GET /admin/store-approvals (PendingStoreApplication on the API). */
export interface StoreApplication {
  organizationId: string;
  name: string;
  /** `pending_verification`, or the legacy `pending_approval`. */
  status: string;
  businessName: string | null;
  website: string | null;
  primaryContactEmail: string | null;
  primaryContactName: string | null;
  address: StoreApplicationAddress | null;
  taxId: string | null;
  brandsSold: string[];
  submittedAt: string | null;
  infoRequest: { message: string; requestedAt: string; requestedBy: string } | null;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await makeApiCall(endpoint, {
    ...options,
    headers: {
      ...(await getAuthHeaders()),
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error?.message || payload.message || `HTTP ${response.status}`);
  }
  return payload.data as T;
}

const path = (organizationId: string, action: string) =>
  `/admin/store-approvals/${encodeURIComponent(organizationId)}/${action}`;

export const storeApprovalApi = {
  listPending: () => request<StoreApplication[]>('/admin/store-approvals'),
  approve: (organizationId: string, notes?: string) =>
    request<unknown>(path(organizationId, 'approve'), {
      method: 'POST',
      body: JSON.stringify({ notes: notes?.trim() || undefined }),
    }),
  reject: (organizationId: string, reason: string) =>
    request<unknown>(path(organizationId, 'reject'), {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  requestInfo: (organizationId: string, message: string) =>
    request<unknown>(path(organizationId, 'request-info'), {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
};

/** Queue case id for a store application, as used in the Approvals `?case=` param. */
export const storeCaseId = (organizationId: string) => `store:${organizationId}`;

/** Store statuses the Approvals queue can decide. Mirrors REVIEWABLE_STORE_STATUSES on the API. */
export const REVIEWABLE_STORE_STATUSES = ['pending_verification', 'pending_approval'];

export const formatStoreAddress = (address: StoreApplicationAddress | null | undefined): string =>
  address
    ? [address.street, address.city, address.state, address.zip || address.zipCode, address.country]
        .filter(Boolean)
        .join(', ')
    : '';
